import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpResponse } from '@angular/common/http';
import {
  ThemeConfig,
  ImageConfig,
  AssetsResponse,
  ColorPalette,
  ContactInfo,
  FontFamily,
} from './theme.types';
import { AppDataService } from 'src/app/core/app-data/app-data.service';
import { ASSETS } from '../../api.constants';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private currentTheme = new BehaviorSubject<ThemeConfig | null>(null);
  private isThemeLoaded = new BehaviorSubject<boolean>(false);
  private countryAssets = new BehaviorSubject<{
    registration: ContactInfo[];
  } | null>(null);

  public theme$ = this.currentTheme.asObservable();
  public isLoaded$ = this.isThemeLoaded.asObservable();
  public countryAssets$ = this.countryAssets.asObservable();

  private readonly THEME_CACHE_KEY = 'app_theme_cache';
  private readonly THEME_CACHE_TTL = 604800000; // 7 days in milliseconds (ETag handles change detection)

  constructor(
    private appDataService: AppDataService,
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Get cached theme if available (ignore expiry, ETag handles freshness)
   */
  private getCachedTheme(domain: string): { theme: ThemeConfig; countryAssets: { registration: ContactInfo[] } | null; etag: string | null } | null {
    try {
      const cached = localStorage.getItem(this.THEME_CACHE_KEY);
      if (!cached) {
        return null;
      }
      
      const { theme, cachedDomain, etag, countryAssets } = JSON.parse(cached);
      const isSameDomain = cachedDomain === domain;
      
      if (isSameDomain) {
        return { theme, countryAssets: countryAssets || null, etag: etag || null };
      }
      
      // Clear cache for different domain
      localStorage.removeItem(this.THEME_CACHE_KEY);
      return null;
    } catch (error) {
      console.error('[Theme Cache] Error reading cache:', error);
      return null;
    }
  }

  /**
   * Cache theme configuration in localStorage
   */
  private cacheTheme(theme: ThemeConfig, domain: string, countryAssets: { registration: ContactInfo[] } | null, etag?: string | null): void {
    try {
      const cacheData = {
        theme,
        timestamp: Date.now(),
        cachedDomain: domain,
        countryAssets,
        etag: etag || null
      };
      localStorage.setItem(this.THEME_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      // Silently fail if localStorage is full or unavailable
      console.warn('[Theme Cache] Failed to cache theme:', error);
    }
  }

  /**
   * Load theme configuration from API based on domain
   * Uses ETag-based conditional requests for efficient cache validation
   * @param domain - Domain to load theme for (defaults to current hostname)
   * @param skipBackgroundRefresh - If true, skips background refresh when cache has no ETag (useful during SSO)
   */
  loadThemeFromAPI(domain?: string, skipBackgroundRefresh = false): Observable<ThemeConfig | null> {
    const currentDomain = domain || window.location.hostname;
    
    // Check cached theme
    const cached = this.getCachedTheme(currentDomain);
    if (cached) {
      if (cached.etag) {
        // Restore country assets from cache
        if (cached.countryAssets) {
          this.countryAssets.next(cached.countryAssets);
        }
        
        return from(this.applyTheme(cached.theme)).pipe(
          tap(() => {
            this.currentTheme.next(cached.theme);
            this.isThemeLoaded.next(true);
            
            // Validate in background - will update if changed
            this.validateCacheWithETag(currentDomain, cached.theme, cached.countryAssets, cached.etag!)
              .subscribe({
                error: error => console.error('[Theme Cache] Validation error:', error)
              });
          }),
          map(() => cached.theme)
        );
      } else {
        // Has cache but no ETag - apply cached theme and fetch fresh in background
        
        // Restore country assets from cache
        if (cached.countryAssets) {
          this.countryAssets.next(cached.countryAssets);
        }
        
        return from(this.applyTheme(cached.theme)).pipe(
          tap(() => {
            this.currentTheme.next(cached.theme);
            this.isThemeLoaded.next(true);
            
            // Validate cache with ETag in background - updates if assets changed
            if (cached.etag) {
              this.validateCacheWithETag(currentDomain, cached.theme, cached.countryAssets, cached.etag).subscribe(
                freshTheme => {
                  if (freshTheme) {
                    console.log('[Theme Cache] Theme updated from server (ETag mismatch)');
                  } else {
                    console.log('[Theme Cache] Theme is up-to-date (ETag match)');
                  }
                },
                error => {
                  if (error?.status === 304) {
                    console.log('[Theme Cache] Theme is up-to-date (304 Not Modified)');
                  } else {
                    console.error('[Theme Cache] ETag validation error:', error);
                  }
                }
              );
            } else if (!skipBackgroundRefresh || !cached.countryAssets?.registration?.length) {
              // No ETag in cache, fetch fresh. Normally skipped during SSO, but
              // we must still fetch when the cached country assets are missing or
              // empty — otherwise SSO users get a blank Contact Us popup (the
              // country assets never get populated because the refresh is skipped).
              this.fetchThemeFromAPI(currentDomain).subscribe(
                freshTheme => {
                  if (freshTheme) {
                    console.log('[Theme Cache] Fresh theme fetched and applied (no ETag)');
                  }
                },
                error => console.error('[Theme Cache] Fetch error:', error)
              );
            } else {
              console.log('[Theme Cache] Skipping background refresh (SSO authentication)');
            }
          }),
          map(() => cached.theme)
        );
      }
    }
    
    // No cache - setup default theme and fetch fresh
    console.log('[Theme Cache] No cache found, fetching fresh theme');
    this.setupDefaultTheme();
    return this.fetchThemeFromAPI(currentDomain);
  }

  /**
   * Validate cached theme using ETag conditional request (If-None-Match)
   */
  private validateCacheWithETag(
    domain: string,
    cachedTheme: ThemeConfig,
    cachedCountryAssets: { registration: ContactInfo[] } | null,
    etag: string
  ): Observable<ThemeConfig | null> {
    const url = this.formatThemeUrl(domain);
    const headers = {
      'If-None-Match': etag,
      'Cache-Control': 'no-cache'
    };

    return this.http.get<AssetsResponse>(url, { headers, observe: 'response' }).pipe(
      switchMap((response: HttpResponse<AssetsResponse>) => {
        // Server returned 200 - content changed, process new theme
        const newEtag = response.headers.get('ETag');
        const config = response.body;
        if (config) {
          // Prefer fresh registration data, but never overwrite existing good
          // country assets with an empty/missing response — that would blank the
          // Contact Us popup even though valid data is already cached.
          const freshRegistration = config.country_asset?.registration;
          const newCountryAssets = (freshRegistration && freshRegistration.length)
            ? { registration: freshRegistration }
            : this.countryAssets.value;
          if (newCountryAssets) {
            this.countryAssets.next(newCountryAssets);
          }
          return this.setupConfig(config).pipe(
            switchMap((themeConfig: ThemeConfig | null) => {
              if (themeConfig) {
                return from(this.applyTheme(themeConfig)).pipe(
                  tap(() => {
                    this.currentTheme.next(themeConfig);
                    this.isThemeLoaded.next(true);
                    this.cacheTheme(themeConfig, domain, newCountryAssets, newEtag);
                  }),
                  map(() => themeConfig)
                );
              }
              return of(null);
            })
          );
        }
        return of(null);
      }),
      catchError((error) => {
        // 304 Not Modified - use cached theme
        if (error?.status === 304) {
          this.currentTheme.next(cachedTheme);
          if (cachedCountryAssets) {
            this.countryAssets.next(cachedCountryAssets);
          }
          this.isThemeLoaded.next(true);
          // Update timestamp to extend cache
          this.cacheTheme(cachedTheme, domain, cachedCountryAssets, etag);
          return of(cachedTheme);
        }
        // Other errors - fall back to cached theme
        this.currentTheme.next(cachedTheme);
        if (cachedCountryAssets) {
          this.countryAssets.next(cachedCountryAssets);
        }
        this.isThemeLoaded.next(true);
        return of(cachedTheme);
      })
    );
  }

  /**
   * Fetch theme from API without cache
   */
  private fetchThemeFromAPI(domain: string): Observable<ThemeConfig | null> {
    const url = this.formatThemeUrl(domain);
    const headers = {
      'Cache-Control': 'no-cache'
    };
    
    return this.http.get<AssetsResponse>(url, { headers, observe: 'response' }).pipe(
      switchMap((response: HttpResponse<AssetsResponse>) => {
        const etag = response.headers.get('ETag');
        const config = response.body;
        if (config) {
          // Prefer fresh registration data, but never overwrite existing good
          // country assets with an empty/missing response — that would blank the
          // Contact Us popup even though valid data is already cached.
          const freshRegistration = config.country_asset?.registration;
          const newCountryAssets = (freshRegistration && freshRegistration.length)
            ? { registration: freshRegistration }
            : this.countryAssets.value;
          if (newCountryAssets) {
            this.countryAssets.next(newCountryAssets);
          }
          return this.setupConfig(config).pipe(
            switchMap((themeConfig: ThemeConfig | null) => {
              if (themeConfig) {
                return from(this.applyTheme(themeConfig)).pipe(
                  tap(() => {
                    this.currentTheme.next(themeConfig);
                    this.isThemeLoaded.next(true);
                    this.cacheTheme(themeConfig, domain, newCountryAssets, etag);
                  }),
                  map(() => themeConfig)
                );
              }
              return of(null);
            })
          );
        }
        return of(null);
      }),
      catchError((error) => {
        console.warn(
          '[Theme Cache] Failed to load theme from API, checking localStorage for fallback',
          error
        );
        
        // Try to load from cache as fallback when API fails
        const cached = this.getCachedTheme(domain);
        if (cached && cached.theme) {
          
          // Restore country assets from cache
          if (cached.countryAssets) {
            this.countryAssets.next(cached.countryAssets);
          }
          
          return from(this.applyTheme(cached.theme)).pipe(
            tap(() => {
              this.currentTheme.next(cached.theme);
              this.isThemeLoaded.next(true);
            }),
            map(() => cached.theme)
          );
        }
        
        return of(null);
      })
    );
  }

  /**
   * Format theme API URL with domain parameter
   */
  private formatThemeUrl(domain: string): string {
    return `${ASSETS}?domain=${encodeURIComponent(domain)}`;
  }

  private setupConfig(config: AssetsResponse): Observable<ThemeConfig | null> {
    const { default_asset } = config;
    const currentDomain = window.location.hostname;
    const primaryPalette = this.generateColorPalette(
      default_asset.styles.primary_color || '#3f51b5'
    );
    const secondaryPalette = this.generateColorPalette(
      default_asset.styles.secondary_color || '#f50057'
    );
    const accentPalette = this.generateColorPalette(
      default_asset.styles.secondary_color || '#ff4081'
    );
    const sidenavPalette = this.generateColorPalette(
      default_asset.styles.sidenav_color || '#ffda34'
    );
    const listPalette = this.generateColorPalette(
      default_asset.styles.list_color || '#303340'
    );
    const font_size = {
      xs: default_asset.styles?.xs,
      sm: default_asset.styles?.sm,
      body: default_asset.styles?.body,
      h1: default_asset.styles?.h1,
      h2: default_asset.styles?.h2,
      h3: default_asset.styles?.h3,
      h4: default_asset.styles?.h4,
      h5: default_asset.styles?.h5,
      h6: default_asset.styles?.h6,
    };
    const themeConfig: ThemeConfig = {
      name: default_asset.branding?.site_name || 'Default Theme',
      domain: currentDomain,
      siteName:
        default_asset.branding?.site_name ||
        'Assurant Commercial Equipment Protection',
      colors: {
        primary: primaryPalette,
        secondary: secondaryPalette,
        accent: accentPalette,
        sidenav: sidenavPalette,
        list: listPalette,
        sidenav_font: default_asset.styles.sidenav_font_color || '#FFFFFF',
        list_font: default_asset.styles.list_font_color || '#FFFFFF',
        sidenav_logo_background:
          default_asset.styles.sidenav_logo_background || null,
      },
      typography: {
        primary: {
          name: default_asset.styles.primary_font_family || 'Arial, sans-serif',
          url: default_asset.styles.primary_font_url,
        },
        heading: {
          name: default_asset.styles.header_font_family || 'Arial, sans-serif',
          url: default_asset.styles.header_font_url || '',
        },
      },
      images: {
        brandLogo: default_asset.images?.brand_logo || '',
        iconLogo: default_asset.images?.brand_logo || '',
        backgroundBrand: default_asset.images?.landing_logo || '',
        registrationGraphic: default_asset.images?.registration_graphic || '',
        loginGraphic: default_asset.images?.login_graphic || '',
        brandTagline: default_asset.branding?.brand_tagline || null,
        sidenavLogo: default_asset.images?.sidenav_logo || null,
      },
      favicon: default_asset.images?.favicon || '',
      border_radius: default_asset.styles?.border_radius || '0px',
      register_graphic_position: default_asset.styles?.registration_graphic_position,
      login_graphic_position: default_asset.styles?.login_graphic_position,
      font_size,
    };
    this.currentTheme.next(themeConfig);
    this.isThemeLoaded.next(true);
    return of(themeConfig);
  }

  /**
   * Apply theme configuration to the application
   */
  async applyTheme(config: ThemeConfig): Promise<void> {
    // Ensure Material theme CSS is loaded
    this.loadDefaultTheme('theme-common.css');
    
    this.applyColors(config);
    // Load primary font
    this.loadFont(config.typography.primary);
    // Load header font
    this.loadFont(config.typography.heading);
    this.applyTypography(config);
    this.applyBorderRadius(config);
    // Wait for images to load
    await this.loadImages(config.images);
    this.updateFavicon(config.favicon);
    this.updateTitle(config.siteName);
    this.updateGraphicPositions(config);
    this.updateFontSize(config);
  }
  /**
   * @param config - Theme configuration
   * Update graphic positions for registration and login pages
   */
  private updateGraphicPositions(config: ThemeConfig): void {
    const root = this.document.documentElement;
    if (config.register_graphic_position) {
      root.style.setProperty(
        '--register-graphic-position',
        config.register_graphic_position
      );
    }
    if (config.login_graphic_position) {
      root.style.setProperty(
        '--login-graphic-position',
        config.login_graphic_position
      );
    }
  }
  /**
   * Apply CSS custom colors properties for dynamic styling
   */
  private applyColors(config: ThemeConfig): void {
    const root = this.document.documentElement;
    // Primary colors
    if (this.isValidColorPalette(config.colors.primary)) {
      root.style.setProperty('--dynamic-primary', config.colors.primary.main);
      root.style.setProperty('--theme-primary', config.colors.primary.main);
      root.style.setProperty(
        '--dynamic-primary-light',
        config.colors.primary.light
      );
      root.style.setProperty(
        '--theme-primary-light',
        config.colors.primary.light
      );
      root.style.setProperty(
        '--dynamic-primary-dark',
        config.colors.primary.dark
      );
      root.style.setProperty(
        '--theme-primary-dark',
        config.colors.primary.dark
      );
      root.style.setProperty(
        '--theme-primary-contrast',
        config.colors.primary.contrast
      );

      // Convert primary color to RGB values
      const primaryRgb = this.hexToRgb(config.colors.primary.main);
      if (primaryRgb) {
        root.style.setProperty(
          '--dynamic-primary-rgb',
          `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`
        );
        root.style.setProperty(
          '--theme-primary-rgb',
          `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`
        );
      }
    } else if (config.colors.primary) {
      console.warn('[Theme] Skipping invalid primary palette; keeping existing theme colors.');
    }

    // Secondary colors
    if (this.isValidColorPalette(config.colors.secondary)) {
      root.style.setProperty(
        '--dynamic-secondary',
        config.colors.secondary.main
      );
      root.style.setProperty('--theme-secondary', config.colors.secondary.main);
      root.style.setProperty(
        '--dynamic-secondary-light',
        config.colors.secondary.light
      );
      root.style.setProperty(
        '--theme-secondary-light',
        config.colors.secondary.light
      );
      root.style.setProperty(
        '--dynamic-secondary-dark',
        config.colors.secondary.dark
      );
      root.style.setProperty(
        '--theme-secondary-dark',
        config.colors.secondary.dark
      );
      root.style.setProperty(
        '--theme-secondary-contrast',
        config.colors.secondary.contrast
      );

      // Dynamic caret color
      root.style.setProperty(
        '--theme-caret-search',
        config.colors.secondary.light
      );

      // Convert secondary color to RGB values
      const secondaryRgb = this.hexToRgb(config.colors.secondary.main);
      if (secondaryRgb) {
        root.style.setProperty(
          '--dynamic-secondary-rgb',
          `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`
        );
        root.style.setProperty(
          '--theme-secondary-rgb',
          `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`
        );
      }
    } else if (config.colors.secondary) {
      console.warn(
        '[Theme] Skipping invalid secondary palette; using defaults.'
      );
    }

    // Accent colors
    if (this.isValidColorPalette(config.colors.accent)) {
      root.style.setProperty('--dynamic-accent', config.colors.accent.main);
      root.style.setProperty('--theme-accent', config.colors.accent.main);
      root.style.setProperty(
        '--dynamic-accent-light',
        config.colors.accent.light
      );
      root.style.setProperty(
        '--theme-accent-light',
        config.colors.accent.light
      );
      root.style.setProperty(
        '--dynamic-accent-dark',
        config.colors.accent.dark
      );
      root.style.setProperty('--theme-accent-dark', config.colors.accent.dark);
      root.style.setProperty(
        '--theme-accent-contrast',
        config.colors.accent.contrast
      );

      // Convert accent color to RGB values
      const accentRgb = this.hexToRgb(config.colors.accent.main);
      if (accentRgb) {
        root.style.setProperty(
          '--dynamic-accent-rgb',
          `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`
        );
        root.style.setProperty(
          '--theme-accent-rgb',
          `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`
        );
      }
    } else if (config.colors.accent) {
      console.warn('[Theme] Skipping invalid accent palette; using defaults.');
    }

    // Sidenav colors
    if (this.isValidColorPalette(config.colors.sidenav)) {
      root.style.setProperty('--dynamic-sidenav', config.colors.sidenav.main);
      root.style.setProperty('--theme-sidenav', config.colors.sidenav.main);
      root.style.setProperty(
        '--dynamic-sidenav-light',
        config.colors.sidenav.light
      );
      root.style.setProperty(
        '--theme-sidenav-light',
        config.colors.sidenav.light
      );
      root.style.setProperty(
        '--dynamic-sidenav-dark',
        config.colors.sidenav.dark
      );
      root.style.setProperty(
        '--theme-sidenav-dark',
        config.colors.sidenav.dark
      );
      root.style.setProperty(
        '--theme-sidenav-contrast',
        config.colors.sidenav.contrast
      );
      // Default fallback — overridden below if sidenav_logo_background is set
      root.style.setProperty(
        '--top-nav-icon-color',
        this.getContrastColor(config.colors.sidenav.main)
      );
    } else if (config.colors.sidenav) {
      console.warn('[Theme] Skipping invalid sidenav palette; using defaults.');
    }
    if (
      config.colors.sidenav_font &&
      this.isValidHexColor(config.colors.sidenav_font)
    ) {
      root.style.setProperty(
        '--sidenav-font-color',
        config.colors.sidenav_font
      );
      this.setRgbCssVariable('--sidenav-font-color-rgb', config.colors.sidenav_font);
    } else if (config.colors.sidenav_font) {
      console.warn(
        '[Theme] Skipping invalid sidenav font color; using defaults.'
      );
    }
    if (
      config.colors.sidenav_logo_background &&
      this.isValidHexColor(config.colors.sidenav_logo_background)
    ) {
      root.style.setProperty(
        '--sidenav-logo-background',
        config.colors.sidenav_logo_background
      );
      root.style.setProperty(
        '--top-nav-icon-color',
        this.getContrastColor(config.colors.sidenav_logo_background)
      );
    } else if (config.colors.sidenav_logo_background) {
      console.warn(
        '[Theme] Skipping invalid sidenav logo background color; using defaults.'
      );
    }

    // List colors
    if (this.isValidColorPalette(config.colors.list)) {
      root.style.setProperty('--dynamic-list', config.colors.list.main);
      root.style.setProperty('--theme-list', config.colors.list.main);
      root.style.setProperty('--dynamic-list-light', config.colors.list.light);
      root.style.setProperty('--theme-list-light', config.colors.list.light);
      root.style.setProperty('--dynamic-list-dark', config.colors.list.dark);
      root.style.setProperty('--theme-list-dark', config.colors.list.dark);
      root.style.setProperty(
        '--theme-list-contrast',
        config.colors.list.contrast
      );
    } else if (config.colors.list) {
      console.warn('[Theme] Skipping invalid list palette; using defaults.');
    }
    if (config.colors.list_font && this.isValidHexColor(config.colors.list_font)) {
      root.style.setProperty('--list-font-color', config.colors.list_font);
      this.setRgbCssVariable('--list-font-color-rgb', config.colors.list_font);
    } else if (config.colors.list_font) {
      console.warn('[Theme] Skipping invalid list font color; using defaults.');
    }
  }

  private isValidColorPalette(
    palette?: ColorPalette | null
  ): palette is ColorPalette {
    return !!(
      palette &&
      this.isValidHexColor(palette.main) &&
      this.isValidHexColor(palette.light) &&
      this.isValidHexColor(palette.dark) &&
      this.isValidHexColor(palette.contrast)
    );
  }

  private setRgbCssVariable(variableName: string, colorValue?: string): void {
    if (!colorValue || !this.isValidHexColor(colorValue)) {
      return;
    }

    const normalizedColor = this.normalizeHexColor(colorValue);
    const rgb = this.hexToRgb(normalizedColor);
    if (!rgb) {
      return;
    }

    this.document.documentElement.style.setProperty(
      variableName,
      `${rgb.r}, ${rgb.g}, ${rgb.b}`
    );
  }
  /**
   * Apply typography settings
   * @param config - Theme configuration
   */
  private applyTypography(config: ThemeConfig): void {
    const root = this.document.documentElement;
    // Typography
    if (config.typography) {
      const primaryFontFamily = config.typography.primary.name;
      const secondaryFontFamily =
        config.typography?.heading?.name || primaryFontFamily;

      root.style.setProperty(
        '--dynamic-font-family-primary',
        primaryFontFamily
      );
      root.style.setProperty(
        '--dynamic-font-family-secondary',
        secondaryFontFamily
      );
      root.style.setProperty(
        '--theme-font-family-primary',
        primaryFontFamily
      );
      root.style.setProperty(
        '--theme-font-family-heading',
        secondaryFontFamily
      );
      root.style.setProperty(
        '--theme-font-family-secondary',
        secondaryFontFamily
      );
    }
  }
  /**
   * Apply border radius settings
   * @param config - Theme configuration
   */
  private applyBorderRadius(config: ThemeConfig): void {
    const root = this.document.documentElement;
    // Border radius
    if (config.border_radius) {
      root.style.setProperty('--dynamic-border-radius', config.border_radius);
      root.style.setProperty('--theme-border-radius', config.border_radius);
      root.style.setProperty(
        '--theme-border-radius-sm',
        `calc(${config.border_radius} * 0.5)`
      );
      root.style.setProperty(
        '--theme-border-radius-lg',
        `calc(${config.border_radius} * 1.5)`
      );
      root.style.setProperty(
        '--theme-border-radius-xl',
        `calc(${config.border_radius} * 2)`
      );
      root.style.setProperty(
        '--theme-border-radius-2xl',
        `calc(${config.border_radius} * 3)`
      );
    }
  }

  /**
   * Load Google Fonts or custom font files
   */
  private loadFont(typography?: FontFamily): void {
    if (!typography || !typography.url || !typography.name) return;

    // Check if URL is a CSS file or a direct font file
    const isCssUrl = typography.url.endsWith('.css') || typography.url.includes('fonts.googleapis.com');

    if (isCssUrl) {
      // Handle CSS font files (like Google Fonts)
      this.loadFontFromCss(typography);
    } else {
      // Handle direct font files (.woff2, .woff, .ttf, etc.)
      this.loadFontFromFile(typography);
    }
  }

  /**
   * Load font from CSS URL (like Google Fonts)
   */
  private loadFontFromCss(typography: FontFamily): void {
    const fontId = `custom-font-${typography.name.replace(/\s+/g, '-').toLowerCase()}`;

    // Remove existing font link
    const existingLink = this.document.getElementById(fontId);
    if (existingLink) {
      existingLink.remove();
    }

    // Create and append new font link
    const link = this.document.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    link.href = typography.url;
    this.document.head.appendChild(link);
  }

  /**
   * Load font from direct font file URL
   */
  private loadFontFromFile(typography: FontFamily): void {
    const fontId = `custom-font-style-${typography.name.replace(/\s+/g, '-').toLowerCase()}`;
    // Remove existing font style
    const existingStyle = this.document.getElementById(fontId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Determine font format from URL
    let fontFormat = 'woff2';
    if (typography.url.includes('.woff2')) {
      fontFormat = 'woff2';
    } else if (typography.url.includes('.woff')) {
      fontFormat = 'woff';
    } else if (typography.url.includes('.ttf')) {
      fontFormat = 'truetype';
    } else if (typography.url.includes('.otf')) {
      fontFormat = 'opentype';
    }

    // Create @font-face CSS rule
    const fontFaceRule = `
      @font-face {
        font-family: '${typography.name}';
        src: url('${typography.url}') format('${fontFormat}');
        font-display: swap;
        font-weight: normal;
        font-style: normal;
      }
    `;

    // Create and append style element
    const style = this.document.createElement('style');
    style.id = fontId;
    style.textContent = fontFaceRule;
    this.document.head.appendChild(style);
  }

  /**
   * Overwrite theme images and preload them
   * Returns a Promise that resolves when all images are loaded
   */
  private async loadImages(images?: ImageConfig): Promise<void> {
    if (!images) {
      return Promise.resolve();
    }
    const root = this.document.documentElement;
    const {
      loginGraphic,
      registrationGraphic,
      backgroundBrand,
      iconLogo,
      brandLogo,
      sidenavLogo,
    } = images;

    // Collect images to preload
    const imagesToPreload = [
      loginGraphic,
      registrationGraphic,
      backgroundBrand,
      iconLogo,
      brandLogo,
      sidenavLogo
    ].filter(Boolean) as string[];

    // Create promises for each image load
    const imageLoadPromises = imagesToPreload.map(src => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Resolve even on error to not block
        img.src = src;
      });
    });

    // Overwrite existing images
    if (loginGraphic) {
      root.style.setProperty('--login-graphic', `url(${loginGraphic})`);
    }
    if (registrationGraphic) {
      root.style.setProperty(
        '--register-graphic',
        `url(${registrationGraphic})`
      );
    }
    if (backgroundBrand) {
      root.style.setProperty('--landing-logo', `url(${backgroundBrand})`);
    }
    // Keep icon-logo independent from landing-logo/brand-logo assets.
    if (iconLogo) {
      root.style.setProperty('--icon-logo', `url(${iconLogo})`);
      root.style.setProperty('--icon-logo-white', `url(${iconLogo})`);
      root.style.setProperty('--icon-logo-small', `url(${iconLogo})`);
      root.style.setProperty('--icon-logo-dark', `url(${iconLogo})`);
    }
    if (brandLogo) {
      root.style.setProperty('--landing-logo', `url(${brandLogo})`);
    }

    if (sidenavLogo) {
      root.style.setProperty('--sidenav-logo', `url(${sidenavLogo})`);
    }
    // Brand tagline
    const brandTagline = images?.brandTagline;
    if (brandTagline) {
      root.style.setProperty('--brand-tagline', `"${brandTagline}"`);
    } else {
      root.style.setProperty('--brand-tagline', '""');
    }

    // Wait for all images to load
    await Promise.all(imageLoadPromises);
  }

  /**
   *
   */
  private updateFontSize(config: ThemeConfig): void {
    const fontSize = config?.font_size;
    const root = this.document.documentElement;
    if (fontSize) {
      fontSize?.xs &&
        root.style.setProperty('--theme-font-size-xs', fontSize.xs);
      fontSize?.sm &&
        root.style.setProperty('--theme-font-size-sm', fontSize.sm);
      fontSize?.body &&
        root.style.setProperty('--theme-font-size-base', fontSize.body);
      fontSize?.h6 &&
        root.style.setProperty('--theme-font-size-xl', fontSize['h6']);
      fontSize?.h5 &&
        root.style.setProperty('--theme-font-size-2xl', fontSize['h5']);
      fontSize?.h4 &&
        root.style.setProperty('--theme-font-size-3xl', fontSize['h4']);
      fontSize?.h3 &&
        root.style.setProperty('--theme-font-size-4xl', fontSize['h3']);
      fontSize?.h2 &&
        root.style.setProperty('--theme-font-size-5xl', fontSize['h2']);
      fontSize?.h1 &&
        root.style.setProperty('--theme-font-size-6xl', fontSize['h1']);
    }
  }

  /**
   * Update favicon
   */
  private updateFavicon(faviconUrl?: string): void {
    if (!faviconUrl) return;
    const head = this.document.getElementsByTagName('head')[0];

    const existingFavicon = this.document.getElementById('client-favicon');
    if (existingFavicon) {
      existingFavicon.remove();
    }

    const style = this.document.createElement('link');
    style.id = 'client-favicon';
    style.rel = 'icon';
    style.href = faviconUrl;

    head.appendChild(style);
  }

  /**
   * Update page title
   */
  private updateTitle(siteName?: string): void {
    if (siteName) {
      const titleElement = this.document.querySelector('title');
      if (titleElement) {
        const currentTitle = titleElement.textContent;
        const basePage = currentTitle?.split(' | ')[1] || '';
        titleElement.textContent = basePage
          ? `${siteName} | ${basePage}`
          : siteName;
      }
    }
  }

  private setupDefaultTheme(): void {
    let themeFile = 'theme-common.css'; // default theme file
    this.updateFavicon('./assets/favicons/assurant-favicon.ico');
    this.loadDefaultTheme(themeFile);
    this.isThemeLoaded.next(true);
  }

  /**
   * Load default theme CSS file
   */
  private loadDefaultTheme(themeFile: string): void {
    const existingLink = this.document.getElementById(
      'client-theme'
    ) as HTMLLinkElement;
    if (existingLink) {
      const currentHref = existingLink.getAttribute('href') || '';
      if (currentHref === themeFile || currentHref.endsWith(`/${themeFile}`)) {
        return;
      }
      existingLink.remove();
    }

    const link = this.document.createElement('link');
    link.id = 'client-theme';
    link.rel = 'stylesheet';
    link.href = themeFile;
    this.document.head.appendChild(link);
  }

  /**
   * Get current theme configuration
   */
  getCurrentTheme(): BehaviorSubject<ThemeConfig | null> {
    return this.currentTheme;
  }
  /**
   * Get country Assets
   */
  getCountryAssets(): { registration: ContactInfo[] } | null {
    return this.countryAssets.value;
  }

  /**
   * Clear theme cache and force reload
   * Useful when assets are updated on the server
   */
  clearThemeCache(): void {
    try {
      localStorage.removeItem(this.THEME_CACHE_KEY);
    } catch (error) {
      console.warn('[Theme Cache] Failed to clear cache:', error);
    }
  }

  /**
   * Force reload theme from server (bypasses cache)
   */
  forceReloadTheme(domain?: string): Observable<ThemeConfig | null> {
    this.clearThemeCache();
    const currentDomain = domain || window.location.hostname;
    return this.fetchThemeFromAPI(currentDomain);
  }

  /**
   * Generate color palette (main, light, dark, contrast) based on a main color
   * @param mainColor - Hex color code (e.g., '#3f51b5')
   * @returns Object containing main, light, dark, and contrast colors in hex format
   */
  generateColorPalette(mainColor: string): ColorPalette | null {
    if (!this.isValidHexColor(mainColor)) {
      console.info(
        'Invalid hex color format. Expected format: #RRGGBB or #RGB'
      );
      return null;
    }
    const normalizedColor = this.normalizeHexColor(mainColor);
    const rgb = this.hexToRgb(normalizedColor);

    if (!rgb) {
      console.warn('Failed to convert hex color to RGB');
      return {
        main: '',
        light: '',
        dark: '',
        contrast: '',
      };
    }

    const hsl = this.rgbToHsl(rgb.r, rgb.g, rgb.b);

    // Generate light variant (increase lightness by 10%)
    const lightHsl = {
      h: hsl.h,
      s: hsl.s,
      l: Math.min(hsl.l + 0.1, 1),
    };

    // Generate dark variant (decrease lightness by 10%)
    const darkHsl = {
      h: hsl.h,
      s: hsl.s,
      l: Math.max(hsl.l - 0.1, 0),
    };

    // Generate contrast color (white or black based on luminance)
    const contrast = this.getContrastColor(normalizedColor);

    return {
      main: normalizedColor,
      light: this.hslToHex(lightHsl.h, lightHsl.s, lightHsl.l),
      dark: this.hslToHex(darkHsl.h, darkHsl.s, darkHsl.l),
      contrast: contrast,
    };
  }

  /**
   * Validate if a string is a valid hex color
   */
  private isValidHexColor(hex: string): boolean {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
  }

  /**
   * Normalize hex color to 6-digit format
   */
  private normalizeHexColor(hex: string): string {
    if (hex.length === 4) {
      // Convert #RGB to #RRGGBB
      return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    return hex.toUpperCase();
  }

  /**
   * Convert hex color to RGB
   */
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  /**
   * Convert RGB to HSL
   */
  private rgbToHsl(
    r: number,
    g: number,
    b: number
  ): { h: number; s: number; l: number } {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return { h, s, l };
  }

  /**
   * Convert HSL to hex color
   */
  private hslToHex(h: number, s: number, l: number): string {
    const hslToRgbComponent = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hslToRgbComponent(p, q, h + 1 / 3);
      g = hslToRgbComponent(p, q, h);
      b = hslToRgbComponent(p, q, h - 1 / 3);
    }

    const toHex = (c: number): string => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  }

  /**
   * Get contrast color (white or black) based on WCAG relative luminance.
   * Uses gamma-corrected sRGB luminance so mid-range saturated colors
   * (e.g. amber #c88b11) are classified correctly.
   */
  private getContrastColor(hex: string): string {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return '#FFFFFF';

    const toLinear = (c: number): number => {
      const s = c / 255;
      return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };

    const luminance =
      0.2126 * toLinear(rgb.r) +
      0.7152 * toLinear(rgb.g) +
      0.0722 * toLinear(rgb.b);

    return luminance > 0.40 ? '#000000' : '#FFFFFF';
  }
}
